/** Full agreement package review: Agreement → Schedule A → Compensation → Acknowledgments → typed legal name. */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { btnGhost, btnPrimary, errorText, inputCls } from '../finance/financeUi';
import { CompensationSummary, ScheduleATables, pdfBlobUrl } from './agreementShared';

const Section = ({ n, title, children, testid }) => (
  <section className="space-y-2 rounded-xl border bg-card p-4" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{n} · {title}</p>{children}
  </section>
);

function AgreementBody({ p, onRead }) {
  const [url, setUrl] = useState(null);
  const fits = (el) => { if (el && el.scrollHeight <= el.clientHeight + 12) onRead(); };
  useEffect(() => {
    if (!p.agreement.has_file) return undefined;
    onRead();
    let u;
    pdfBlobUrl(`/activation/agreement-packages/${p.agreement_type}/file`).then((x) => { u = x; setUrl(x); }).catch((e) => toast.error(errorText(e)));
    return () => u && URL.revokeObjectURL(u);
  }, [p.agreement_type, p.agreement.has_file]);
  if (p.agreement.has_file) return url ? <iframe title="Agreement" src={url} className="h-[480px] w-full rounded-md border" data-testid="package-agreement-pdf" /> : <p className="text-sm">Loading agreement…</p>;
  return <div ref={fits} onScroll={(e) => { const el = e.currentTarget; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) onRead(); }} className="agreement-doc max-h-[420px] overflow-y-auto rounded-md border bg-background p-4" data-testid="package-agreement-text" dangerouslySetInnerHTML={{ __html: p.agreement.body_html }} />;
}

export function AgreementPackageReview({ agreementType, onDone, onCancel }) {
  const [p, setP] = useState(null);
  const [checked, setChecked] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [read, setRead] = useState(false);
  const started = useRef(Date.now());
  useEffect(() => {
    let live = true;
    api.request(`/activation/agreement-packages/${agreementType}`).then((d) => { if (live) { setP(d); setName(''); setChecked([]); } }).catch((e) => { if (live) { toast.error(errorText(e)); onCancel?.(); setP(false); } });
    return () => { live = false; };
  }, [agreementType]); // eslint-disable-line react-hooks/exhaustive-deps
  if (p === false) return <p className="text-sm text-red-700" data-testid="package-load-error">This agreement could not be loaded. Please try again shortly.</p>;
  if (!p) return <p className="text-sm text-muted-foreground">Loading your agreement package…</p>;
  const acks = p.agreement.acknowledgments;
  const ack = p.kind === 'SCHEDULE_A_ACKNOWLEDGMENT';
  const ready = checked.length === acks.length && name.trim().split(/\s+/).length >= 2 && (read || ack);
  const boldsign = p.agreement.signature_method === 'BOLDSIGN';
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.request(`/activation/agreement-packages/${agreementType}/accept`, { method: 'POST', body: JSON.stringify({ legal_name: name, acknowledgments: checked, preview_hash: p.preview_hash, read_receipt: { scrolled_to_end: read, read_seconds: Math.round((Date.now() - started.current) / 1000) } }) });
      toast.success(r.status === 'PENDING_SIGNATURE' ? 'Sent to your email for e-signature' : 'Accepted — your signed package is in My Documents');
      onDone();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="package-review">
      <div><p className="text-lg font-semibold" data-testid="package-title">{p.agreement.title} <span className="text-xs font-normal text-muted-foreground">version {p.agreement.version}</span></p>
        {ack && <p className="mt-1 rounded-md bg-amber-50 p-2 text-sm text-amber-900" data-testid="package-schedule-change">Your Schedule A changed. Review and acknowledge the new schedule — your earlier acceptance stays on record unchanged.</p>}</div>
      {!ack && <Section n="1" title="Full agreement" testid="package-section-agreement"><AgreementBody p={p} onRead={() => setRead(true)} />{!read && <p className="text-xs text-amber-700" data-testid="package-scroll-hint">Scroll to the end of the agreement to enable acceptance.</p>}</Section>}
      <Section n={ack ? '1' : '2'} title="Assigned Schedule A" testid="package-section-schedule"><ScheduleATables scheduleA={p.schedule_a} /></Section>
      <Section n={ack ? '2' : '3'} title="Compensation summary" testid="package-section-compensation"><CompensationSummary comp={p.compensation} label={p.compensation_label} /></Section>
      <Section n={ack ? '3' : '4'} title="Required acknowledgments" testid="package-section-acks">
        {acks.map((a, i) => <label key={a} className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={checked.includes(a)} onChange={(e) => setChecked(e.target.checked ? [...checked, a] : checked.filter((x) => x !== a))} data-testid={`package-ack-${i}`} />{a}</label>)}
      </Section>
      <Section n={ack ? '4' : '5'} title="Typed legal name" testid="package-section-signature">
        <input className={inputCls} placeholder={p.legal_name_hint || 'First and last name'} value={name} onChange={(e) => setName(e.target.value)} data-testid="package-legal-name" />
        <p className="text-xs text-muted-foreground">Signing as {p.email}. We record the time, your IP address and device with this acceptance.</p>
      </Section>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btnPrimary} disabled={!ready || busy} onClick={submit} data-testid="package-accept-btn">{busy ? 'Saving…' : boldsign ? 'Send for e-signature' : 'I Agree & Continue'}</button>
        {onCancel && <button type="button" className={btnGhost} onClick={onCancel} data-testid="package-cancel-btn">Back</button>}
      </div>
    </div>
  );
}
